import { World, type Body, type CollisionEvent } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene, SceneAction } from "./types";
import { box, ball, nGon, polygon, rng } from "./helpers";
import { collisionImpactPoint, fractureBody } from "./fracture";

const MUZZLE_X = -10.8;
const MUZZLE_Y = -1.4;
const MAX_BULLETS = 42;
const FRACTURE_SPEED = 24;

export const bullets: Demo = {
	id: "bullets",
	title: "Bullets",
	blurb:
		"Tiny, fast rounds slam into paper-thin plates instead of tunneling through; only clean shots through the gaps reach the target stack behind them.",
	category: "Action & Destruction",
	build(): Scene {
		const world = new World({
			gravity: [0, 2.4],
			solver: {
				velocityIterations: 5,
				positionIterations: 4,
				substeps: 6,
				contactHertz: 36,
				contactDampingRatio: 9,
				maxContactPushSpeed: 10,
				// Bullets reach ~138 u/s (≈2.3 u/step at 60fps). Sweep the broadphase
				// AABB along their travel so thin plates become speculative contacts
				// instead of being tunneled through in a single step.
				maxSpeculativeSweep: 2.5,
				slop: 0.004,
				correction: 0.22,
				restitutionThreshold: 1,
			},
		});
		box(world, 0, 5.35, 30, 1.0, { isStatic: true, friction: 0.8 });
		box(world, -14.7, -0.8, 1.0, 12.8, { isStatic: true, friction: 0.7 });
		box(world, 14.7, -0.8, 1.0, 12.8, { isStatic: true, friction: 0.7 });
		box(world, 0, -6.8, 30, 0.8, { isStatic: true, friction: 0.7 });

		const random = rng(0xb011e75);
		const breakables = new Set<Body>();
		const generations = new Map<Body, number>();
		const pendingFractures: { target: Body; bullet: Body; point: [number, number]; strength: number }[] = [];

		const addTarget = (body: Body): Body => {
			breakables.add(body);
			generations.set(body, 0);
			return body;
		};

		for (const [x, y, h] of [[-3.6, -3.45, 1.55], [-3.6, 0.35, 1.65], [-3.6, 2.6, 1.7]] as const) {
			addTarget(polygon(world, x, y, [
				[-0.32, -h / 2],
				[0.24, -h / 2 + 0.12],
				[0.32, h / 2 - 0.2],
				[0.08, h / 2],
				[-0.3, h / 2 - 0.16],
				[-0.38, -h / 2 + 0.18],
			], { isStatic: true, friction: 0.9, restitution: 0.01, density: 2.3 }));
		}
		addTarget(polygon(world, 0.2, -5.0, [
			[-0.24, -1.1],
			[0.21, -0.86],
			[0.27, 0.72],
			[0.04, 1.12],
			[-0.25, 0.92],
			[-0.31, -0.72],
		], { isStatic: true, friction: 0.9, restitution: 0.01, density: 2.2 }));
		addTarget(polygon(world, 0.2, -1.0, [
			[-0.22, -0.74],
			[0.22, -0.58],
			[0.29, 0.48],
			[-0.03, 0.78],
			[-0.28, 0.48],
		], { isStatic: true, friction: 0.9, restitution: 0.01, density: 2.2 }));
		addTarget(polygon(world, 0.2, 2.8, [
			[-0.26, -1.14],
			[0.16, -1.06],
			[0.31, -0.3],
			[0.22, 1.06],
			[-0.2, 1.16],
			[-0.32, 0.24],
		], { isStatic: true, friction: 0.9, restitution: 0.01, density: 2.2 }));

		for (let row = 0; row < 4; row++) {
			for (let col = 0; col < 4 - row; col++) {
				const x = 5.4 + col * 0.58 - row * 0.25;
				const y = 3.85 - row * 0.58;
				const shape = (row + col) % 3;
				const target = shape === 0
					? nGon(world, x, y, 5, 0.36, {
						friction: 0.55,
						restitution: 0.05,
						density: 1.65,
					})
					: shape === 1
						? polygon(world, x, y, [
							[-0.31, -0.25],
							[0.27, -0.29],
							[0.34, 0.12],
							[0.1, 0.31],
							[-0.3, 0.26],
						], {
							friction: 0.55,
							restitution: 0.05,
							density: 1.65,
						})
						: box(world, x, y, 0.52, 0.52, {
					friction: 0.55,
					restitution: 0.05,
					density: 1.6,
				});
				addTarget(target);
			}
		}

		const bullets: Body[] = [];
		let aiming = false;
		let aimX = -1.5;
		let aimY = -1.4;
		let volleyTimer = 0.28;
		let volleyShots = 0;

		const pruneBullets = (): void => {
			for (let i = bullets.length - 1; i >= 0; i--) {
				const body = bullets[i]!;
				if (world.bodies.includes(body) && body.position.x <= 13 && body.position.y >= -6.4 && body.position.y <= 4.8) continue;
				if (world.bodies.includes(body)) world.remove(body);
				bullets.splice(i, 1);
			}
			while (bullets.length > MAX_BULLETS) {
				const body = bullets.shift()!;
				if (world.bodies.includes(body)) world.remove(body);
			}
		};

		const fireAt = (targetX: number, targetY: number, speed: number): void => {
			const dx = targetX - MUZZLE_X;
			const dy = targetY - MUZZLE_Y;
			const len = Math.hypot(dx, dy) || 1;
			const vx = (dx / len) * speed;
			const vy = (dy / len) * speed;
			bullets.push(
				ball(world, MUZZLE_X, MUZZLE_Y, 0.09, {
					density: 28,
					friction: 0.02,
					restitution: 0.02,
					angularDamping: 8,
					velocity: [vx, vy],
				}),
			);
			pruneBullets();
		};

		const fireVolley = (): void => {
			for (let i = 0; i < 7; i++) {
				const lane = i % 3;
				const targetY = lane === 0 ? -3.8 : lane === 1 ? 0.55 : 2.0;
				fireAt(5.4 + random() * 1.4, targetY + (random() - 0.5) * 0.42, 96 + random() * 18);
			}
		};

		const queueFracture = (event: CollisionEvent): void => {
			const bullet = bullets.includes(event.bodyA) ? event.bodyA : bullets.includes(event.bodyB) ? event.bodyB : null;
			if (!bullet) return;
			const target = breakables.has(event.bodyA) ? event.bodyA : breakables.has(event.bodyB) ? event.bodyB : null;
			if (!target || target === bullet || (generations.get(target) ?? 0) > 1) return;
			const dx = bullet.velocity.x - target.velocity.x;
			const dy = bullet.velocity.y - target.velocity.y;
			const strength = Math.hypot(dx, dy);
			if (strength < FRACTURE_SPEED || pendingFractures.some((item) => item.target === target)) return;
			pendingFractures.push({ target, bullet, point: collisionImpactPoint(event), strength });
		};

		world.on("collisionStart", queueFracture);

		const actions: SceneAction[] = [{ label: "Fire volley", run: fireVolley }];

		const clearBulletSpin = (): void => {
			for (const bullet of bullets) {
				if (world.bodies.includes(bullet)) bullet.angularVelocity = 0;
			}
		};

		return {
			world,
			camera: new Camera(0, -1.2, 31),
			draggable: false,
			hint: "Drag to aim, release to fire",
			actions: () => actions,
			onBeforeStep(dt): void {
				while (pendingFractures.length > 0) {
					const event = pendingFractures.shift()!;
					if (world.bodies.includes(event.bullet)) world.remove(event.bullet);
					const bulletIndex = bullets.indexOf(event.bullet);
					if (bulletIndex !== -1) bullets.splice(bulletIndex, 1);
					if (!world.bodies.includes(event.target)) continue;
					const generation = generations.get(event.target) ?? 0;
					breakables.delete(event.target);
					const shards = fractureBody(world, event.target, event.point, event.strength, {
						rng: random,
						maxFragments: 9,
						minArea: 0.04,
						minEdge: 0.14,
						minThickness: 0.28,
						impactInset: 0.55,
						shrink: 0.94,
						impulseScale: 0.048,
						maxShardSpeed: 28,
						densityScale: 0.86,
					});
					for (const shard of shards) generations.set(shard, generation + 1);
				}
				pruneBullets();
				if (volleyShots >= 9) return;
				volleyTimer -= dt;
				if (volleyTimer > 0) return;
				volleyTimer += 0.18;
				const targetY = volleyShots % 3 === 0 ? -3.8 : volleyShots % 3 === 1 ? 0.55 : 2.0;
				fireAt(5.2 + random() * 1.6, targetY + (random() - 0.5) * 0.32, 92 + random() * 16);
				volleyShots++;
			},
			update(dt): number {
				const steps = world.update(dt);
				clearBulletSpin();
				return steps;
			},
			onPointerDown(x, y): void {
				aiming = true;
				aimX = x;
				aimY = y;
			},
			onPointerMove(x, y): void {
				if (!aiming) return;
				aimX = x;
				aimY = y;
			},
			onPointerUp(x, y): void {
				if (!aiming) return;
				aiming = false;
				aimX = x;
				aimY = y;
				const drag = Math.hypot(x - MUZZLE_X, y - MUZZLE_Y);
				fireAt(x, y, Math.max(82, Math.min(138, drag * 13)));
			},
			draw(ctx, camera): void {
				ctx.save();
				ctx.fillStyle = "#7c5cff";
				ctx.strokeStyle = "rgba(124, 92, 255, 0.82)";
				ctx.lineWidth = 2;
				ctx.beginPath();
				ctx.arc(camera.worldToScreenX(MUZZLE_X), camera.worldToScreenY(MUZZLE_Y), 6, 0, Math.PI * 2);
				ctx.fill();
				if (aiming) {
					ctx.setLineDash([8, 6]);
					ctx.beginPath();
					ctx.moveTo(camera.worldToScreenX(MUZZLE_X), camera.worldToScreenY(MUZZLE_Y));
					ctx.lineTo(camera.worldToScreenX(aimX), camera.worldToScreenY(aimY));
					ctx.stroke();
				}
				ctx.restore();
			},
		};
	},
};